/**
 * Build a deep link to an Events tab, optionally pre-filtered by
 * service / pool / replica.
 *
 * Used by the small "events" links we sprinkle next to entities
 * across the UI (replica rows on PoolReplicas, the section header on
 * PoolDashboard / ServiceDashboard, PodInfo, ...). Centralising the
 * URL shape here keeps the contract with `EventsStream` (which reads
 * the same `service_id` / `pool_id` / `replica_id` query params from
 * `useSearchParams`) in one place — adding a new scope filter later
 * (e.g. `kind`) means changing this helper and the events page,
 * nothing else.
 *
 * Two link modes:
 *
 *   1. Default (no `focus`): land on the Global Events tab. Used
 *      when there is no current entity context to preserve, or
 *      when the operator explicitly wants the unconstrained view.
 *
 *   2. Scoped (`focus` provided): land on the per-entity Events
 *      tab (focus=service:N&tab=events / focus=pool:N&tab=events /
 *      focus=pod:N&tab=events). Keeps the operator inside the same
 *      SubNav rail they were already on, and lets us layer
 *      removable URL filters on top of the locked scope (e.g.
 *      "events for *this* replica" while focused on the pool —
 *      `?focus=pool:5&tab=events&replica_id=42`).
 */
import type { Focus } from "./focus";
import { serializeFocus } from "./focus";

export interface EventsFilter {
  service_id?: number | null;
  pool_id?: number | null;
  replica_id?: number | null;
  /**
   * When provided, the link lands on this entity's per-scope
   * Events tab. When omitted, the link lands on the Global Events
   * tab — same behaviour the helper has had since the events tab
   * was first introduced.
   */
  focus?: Focus;
}

export const EVENTS_FILTER_PARAMS = [
  "service_id",
  "pool_id",
  "replica_id",
] as const;

export type EventsFilterParam = (typeof EVENTS_FILTER_PARAMS)[number];

export function eventsHref(filter: EventsFilter = {}): string {
  const sp = new URLSearchParams();
  // The destination is always *some* events tab — either the
  // Global one (no focus) or a per-entity one (focus set).
  const focusValue = filter.focus ? serializeFocus(filter.focus) : null;
  if (focusValue) sp.set("focus", focusValue);
  sp.set("tab", "events");
  if (filter.service_id != null) {
    sp.set("service_id", String(filter.service_id));
  }
  if (filter.pool_id != null) {
    sp.set("pool_id", String(filter.pool_id));
  }
  if (filter.replica_id != null) {
    sp.set("replica_id", String(filter.replica_id));
  }
  return `/?${sp.toString()}`;
}
